import { TextController } from '../../components/TextController';
import { WordBank } from '../../components/WordBank';
import { useCallback, useImperativeHandle, useRef } from 'react';
import { Word } from '../../components/Word';
import { useSelector } from 'react-redux';
import useFibConfig from '../../hooks/useFibConfig';
import { useGameSounds } from '@educaplay/core';
import { COMPLETAR_SOUNDS } from '../../utils/constants';
import { fullTrim } from '@educaplay/core/utils';

export const WrittingGame = (props) => {
    const { gameState, setGameState, setHasFocus, animated, ref } = props;
    const timer = useRef(null);
    const wordBankRef = useRef(null);
    const textControllerRef = useRef(null);
    const { keySensitive } = useFibConfig();

    const gameSounds = useGameSounds();

    const currentQuestion = useSelector(state => state.game.questions[state.game.currentIndex]);

    useImperativeHandle(ref, () => ({
        entryAnimation: async () => {
            if(!currentQuestion.hideWordBank) {
                wordBankRef.current.entryAnimation();
            }
            await textControllerRef.current.entryAnimation({ delay: .3 });
        },
        checkAnimation: async (userAnswer) => {
            const wordIds = Object.keys(userAnswer);
            
            for (let i = 0; i < wordIds.length; i++) {
                const id = wordIds[i];
                const status = userAnswer[id];

                const word = textControllerRef.current?.getWordRef(id); 
                if(!word) continue;

                if (status === 'correct') {
                    await word.success();
                } else if (status === 'wrong') {
                    await word.fail();
                } else {
                    await word.empty();
                }
            }
        }
    }));

    const handleOnChange = (id, value) => {
        gameSounds.play(COMPLETAR_SOUNDS.WRITE)
        const newValue = value.replace(/\n/g, '')
        const newTextBlanks = gameState.textBlanks.map(blank => {
            if (blank.id === id) {
                return {
                    ...blank,
                    userAnswer: newValue ?? null
                }
            }
            return blank;
        })

        let writtenWords = newTextBlanks
            .map(block => block.userAnswer)
            .filter(Boolean)
            .map(userAnswer => {
                const normalizaedUserAnswer = keySensitive.accent ? fullTrim(userAnswer) : fullTrim(userAnswer.normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
                const capsUserAnswer = keySensitive.caps? normalizaedUserAnswer : normalizaedUserAnswer.toLowerCase();
                return capsUserAnswer;
            })

        const newGameState = {
            ...gameState,
            textBlanks: newTextBlanks,
            words: gameState.words.map(word => {
                const normalizaedWord = keySensitive.accent ? fullTrim(word.data) : fullTrim(word.data.normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
                const capsWord = keySensitive.caps? normalizaedWord : normalizaedWord.toLowerCase();
                if (writtenWords.includes(capsWord)) {
                    const wordIndex = writtenWords.findIndex(writtenWord => writtenWord === capsWord);
                    writtenWords = writtenWords.slice(0, wordIndex).concat(writtenWords.slice(wordIndex + 1))
                    return {
                        ...word,
                        used: true
                    }
                }
                return {
                    ...word,
                    used: false
                }
            })
        }

        // Solo se ejecuta si todas las palabras han sido usadas, evita que se ejecute mientras escribe la última palabra
        // if (newGameState.words.every(word => word.used)) {
        //     onCompleted();
        // }

        setGameState(newGameState);
    }

    const handleBlur = useCallback(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            setHasFocus(false);
        }, 100);
    }, [setHasFocus]);

    const handleFocus = useCallback(() => {
        clearTimeout(timer.current);
        setHasFocus(true);
    }, [setHasFocus]);

    return (
        <>
            {!currentQuestion.hideWordBank &&
                <WordBank ref={wordBankRef} animated={animated} >
                    {gameState.words.map((word) => (
                        <Word 
                            key={word.id}
                            word={word}
                            place={'wordBank'}
                            mode={'writting'}
                            styles={{
                                ghost: word.used,
                                correct: word.isCorrect
                            }}
                        />
                    ))}
                </WordBank>
            }

            <TextController
                ref={textControllerRef}
                onChange={handleOnChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                gameState={gameState}
                setGameState={setGameState}
                animated={animated}
            />
        </>
    )
}